import React, { useCallback, useRef } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import type { EditorInjectionAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';
import { parseUniversalSkillEdges } from '@atlassian/rovo-skills/utils/parse-skill';

import { useSkillsManifest } from '../../../ui/skills-editor-extension/SkillsEditorExtension';

import type { composableEditorChat_GetAvailableSkillsQuery } from './__generated__/composableEditorChat_GetAvailableSkillsQuery.graphql';
import type { composableEditorChat_GetDirectoryItemsQuery } from './__generated__/composableEditorChat_GetDirectoryItemsQuery.graphql';

// Check if we're at 90% of the API limit (100 items)
const API_LIMIT = 100;
const PAGINATION_THRESHOLD = API_LIMIT * 0.9; // 90 items

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EditorApiWithTypeAhead = EditorInjectionAPI<any, any> | null | undefined;

export const ComposableEditorWithSkills = ({
	isConversationAgentic,
	promptHasSkill,
	enableSkills = true,
	siteId,
	editorApi,
	...props
}: EditorNextProps & {
	siteId: string;
	promptHasSkill: boolean;
	isConversationAgentic: boolean;
	enableSkills: boolean;
	editorApi?: EditorApiWithTypeAhead;
}): JSX.Element => {
	const legacySkillsResponse = useLazyLoadQuery<composableEditorChat_GetDirectoryItemsQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseSkillEdges */
		graphql`
			query composableEditorChat_GetDirectoryItemsQuery(
				$first: Int
				$after: String
				$dimensionIds: [ID!]
				$searchKeyword: String
				$relevantFor: [GraphIntegrationDirectoryItemType!]!
				$cloudId: ID!
				$surface: GraphIntegrationSurface
				$shouldFetch: Boolean = false
			) {
				graphIntegration_componentDirectoryItems(
					first: $first
					after: $after
					dimensionIds: $dimensionIds
					searchKeyword: $searchKeyword
					relevantFor: $relevantFor
					cloudId: $cloudId
					surface: $surface
				)
					@optIn(to: "GraphIntegrationItemsQuery")
					@connection(key: "Skills__graphIntegration_componentDirectoryItems")
					@include(if: $shouldFetch) {
					edges {
						node {
							__typename
							... on GraphIntegrationSkillDirectoryItem {
								id
								slashCommand
								name
								description
								iconUrl
								integrationKey {
									displayName
									iconUrl
								}
								color
								tags {
									displayName
								}
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					error {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: API_LIMIT,
			relevantFor: ['SKILL'],
			cloudId: siteId,
			surface: 'ROVO',
			shouldFetch: enableSkills && !fg('rovo_chat_unified_skills_agg'),
		},
	)?.graphIntegration_componentDirectoryItems;

	const unifiedSkillsResponse = useLazyLoadQuery<composableEditorChat_GetAvailableSkillsQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by parseUniversalSkillEdges and kept for future use */
		graphql`
			query composableEditorChat_GetAvailableSkillsQuery(
				$first: Int
				$after: String
				$cloudId: ID!
				$shouldFetch: Boolean = false
			) {
				convoai_availableSkills(first: $first, after: $after, cloudId: $cloudId)
					@optIn(to: "ConvoAiAvailableSkills")
					@connection(key: "Skills__convoai_availableSkills")
					@include(if: $shouldFetch) {
					edges {
						node {
							__typename
							id
							name
							description
							displayName
							slashCommand
							iconUrl
							iconKey
							color
							source
							tags
							category
							integrationKeys
							integrations {
								key
								id
								displayName
								iconUrl
							}
							version
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
					totalCount
					metadata {
						anthropicCount
						stratusCount
						isDegraded
					}
					errors {
						message
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			first: API_LIMIT,
			cloudId: siteId,
			shouldFetch: enableSkills && fg('rovo_chat_unified_skills_agg'),
		},
	)?.convoai_availableSkills;

	const skillsResponse = fg('rovo_chat_unified_skills_agg')
		? unifiedSkillsResponse
		: legacySkillsResponse;

	const skillsPaginationRef = useRef<boolean>(false);

	// Handle errors from both legacy and unified responses
	if (skillsResponse) {
		// Legacy error format
		if ('error' in skillsResponse && skillsResponse.error) {
			const err = skillsResponse.error;
			const errExtensions = err.extensions
				? `[${err.extensions?.map((ext) => `${ext?.statusCode} - ${ext?.errorType}`)?.join(', ')}] `
				: '';
			throw new Error(
				err.message || errExtensions ? `${errExtensions}${err.message}` : 'Failed to load skills',
			);
		}

		// Unified skills error format
		if ('errors' in skillsResponse && skillsResponse.errors && skillsResponse.errors.length > 0) {
			const errorMessages = skillsResponse.errors.map((err) => err.message).join(', ');
			throw new Error(errorMessages || 'Failed to load skills');
		}
	}

	const skillCount = skillsResponse?.edges?.length ?? 0;

	if (skillCount >= PAGINATION_THRESHOLD && !skillsPaginationRef.current) {
		logException({
			exception: new Error(
				`Skills API limit threshold reached: ${skillCount} of ${API_LIMIT} items returned. Pagination is needed.`,
			),
			name: 'aiMateBrowseSkillsPaginationLimit',
			tags: {
				skillCount: skillCount.toString(),
				apiLimit: API_LIMIT.toString(),
			},
		});
		// prevent more than one log per render when threshold is reached
		skillsPaginationRef.current = true;
	}

	const skillsListAferLoad: Skill[] = React.useMemo(
		() => parseUniversalSkillEdges(skillsResponse?.edges ?? null),
		[skillsResponse?.edges],
	);

	const editorExtensionManifest = useSkillsManifest({
		skills: skillsListAferLoad,
		isConversationAgentic,
		promptHasSkill,
		enableSkills,
		siteId,
	});

	const { sendAnalyticsEvent } = useAnalytics();
	const typeAheadWasOpenRef = useRef(false);

	const onTypeAheadPluginState = useCallback(
		({ typeAheadState }: { typeAheadState?: { isOpen?: boolean } }) => {
			const isOpen = Boolean(typeAheadState?.isOpen);
			if (isOpen && !typeAheadWasOpenRef.current) {
				sendAnalyticsEvent({
					eventType: 'screen',
					name: 'rovoChatEditorTypeAhead',
				});
			}
			typeAheadWasOpenRef.current = isOpen;
		},
		[sendAnalyticsEvent],
	);

	usePluginStateEffect(editorApi, ['typeAhead'], onTypeAheadPluginState, {
		disabled: !editorApi,
	});

	return <ComposableEditor extensionProviders={editorExtensionManifest} {...props} />;
};
